/**
 * This utility disables console logs in production environments
 * while preserving them in development for debugging purposes.
 * 
 * To be imported early in the application bootstrap process.
 */
export const disableConsoleLogs = (isProduction: boolean = process.env.NODE_ENV === 'production'): void => {
  if (isProduction) {
    // Store original console methods
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
      debug: console.debug,
    };

    // Replace with no-op functions but keep error logging for critical issues
    console.log = () => {};
    console.info = () => {};
    console.debug = () => {};
    console.warn = () => {};
    
    // Optionally keep error logging for critical issues
    // Comment out the line below if you want to disable error logs too
    // console.error = () => {};
    
    // Or use this to preserve error logs
    console.error = (...args) => {
      // Optionally add additional handling, such as reporting to an error tracking service
      // Only log errors in production if they're critical
      if (args[0] && typeof args[0] === 'string' && args[0].includes('CRITICAL:')) {
        originalConsole.error(...args);
      }
    };
    
    // Log once that console logging is disabled
    originalConsole.log('Console logging disabled in production mode')
  }
};

// Export a function to re-enable console logs (useful for testing)
export const enableConsoleLogs = (): void => {
  // This will only work if the console methods have been replaced by our functions
  if (typeof console.log === 'function' && console.log.toString().includes('() => {}')) {
    // Restore original console methods
    Object.assign(console, {
      log: Function.prototype.bind.call(console.log, console),
      warn: Function.prototype.bind.call(console.warn, console),
      error: Function.prototype.bind.call(console.error, console),
      info: Function.prototype.bind.call(console.info, console),
      debug: Function.prototype.bind.call(console.debug, console),
    });
  }
};
